import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const numberNames: { [key: number]: string } = {
    1: "One", 2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten",
    11: "Eleven", 12: "Twelve", 13: "Thirteen", 14: "Fourteen", 15: "Fifteen", 16: "Sixteen", 17: "Seventeen", 18: "Eighteen", 19: "Nineteen", 20: "Twenty",
    30: "Thirty", 40: "Forty", 50: "Fifty", 60: "Sixty", 70: "Seventy", 80: "Eighty", 90: "Ninety", 100: "One Hundred"
};

const getNumberName = (num: number) => {
    if (numberNames[num]) return numberNames[num];
    if (num > 20 && num < 100) {
        const tens = Math.floor(num / 10) * 10;
        const ones = num % 10;
        return ones === 0 ? numberNames[tens] : `${numberNames[tens]}-${numberNames[ones]}`;
    }
    return "";
};

const NumberNames: React.FC = () => {
    const [questionNumber, setQuestionNumber] = useState(0);
    const [correctAnswer, setCorrectAnswer] = useState("");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Number Names Worksheet for LKG | Learn One to Hundred | Mama’s Guidance";
    }, []);

    const generateQuestion = () => {
        const num = Math.floor(Math.random() * 100) + 1;
        const correct = getNumberName(num);
        setQuestionNumber(num);
        setCorrectAnswer(correct);

        const incorrectOptions = new Set<string>();
        while (incorrectOptions.size < 2) {
            const randNum = Math.floor(Math.random() * 100) + 1;
            const randName = getNumberName(randNum);
            if (randNum !== num) incorrectOptions.add(randName);
        }

        const optionsArray = shuffleArray([correct, ...Array.from(incorrectOptions)]);
        setOptions(optionsArray);
        setMessage("");
    };

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG children learn number names with engaging activities on Mama’s Guidance—designed to reinforce number recognition and spelling through fun, interactive worksheets and visual learning tools.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Number Names</h1>

                                    <p>
                                        Number names are the words we use to represent numbers — like <strong>1 = one</strong>, <strong>2 = two</strong>, and so on. At the LKG level, learning number names from 1 to 100 is a great way to strengthen both numeracy and early literacy skills. This worksheet helps children match numbers with their names, trace them, and recognize them in everyday contexts.
                                    </p>

                                    <h2>🔤 What Are Number Names?</h2>
                                    <p>
                                        Number names are written words for digits. They help children associate quantities with both symbols and spellings, which improves cognitive understanding of numbers and prepares them for word problems later in school.
                                    </p>

                                    <p><strong>Examples:</strong></p>
                                    <ul>
                                        <li>1 → one</li>
                                        <li>5 → five</li>
                                        <li>10 → ten</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify numbers from 1 to 100</li>
                                        <li>Recognize and spell number names correctly</li>
                                        <li>Trace number names to improve writing</li>
                                        <li>Match numerals with corresponding words</li>
                                    </ul>

                                    <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Trace the number names from one to ten</li>
                                        <li>Match numbers with their names (<strong>e.g., 7 → seven</strong>)</li>
                                        <li>Fill in missing letters in number names</li>
                                        <li>Colorful images to count and label with number names</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                    <ul>
                                        <li>Say the number and its name out loud with your child daily</li>
                                        <li>Write flashcards with numbers on one side and names on the other</li>
                                        <li>Use number rhymes and spelling songs to make it fun</li>
                                        <li>Label toys or objects at home with number names</li>
                                    </ul>

                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Let your child form number names using magnetic alphabets or letter cutouts. <strong>“Make the word for 3 – T-H-R-E-E!”</strong> This makes learning hands-on and fun!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Counting Numbers</h1>

                                <p>
                                    Counting is one of the first and most important math skills a child learns. At the LKG level, children are introduced to numbers from 1 to 50 using <strong>real-life objects, fun visuals, and hands-on activities</strong>. This worksheet helps young learners practice counting through engaging and simple exercises.
                                </p>

                                <h2>🔢 What Is Counting?</h2>
                                <p>
                                    Counting means saying numbers in the correct order while keeping track of objects. It&apos;s the foundation for all future math skills, such as <strong>addition, subtraction, and number comparison</strong>. Children first learn to <strong>count forward</strong> using fingers, toys, or pictures.
                                </p>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Recognizing numbers from 1 to 50</li>
                                    <li>Counting real or illustrated objects correctly</li>
                                    <li>Understanding the value each number represents</li>
                                    <li>Building concentration and number sense</li>
                                </ul>

                                <h2>🖍️ Worksheet Highlights</h2>
                                <ul>
                                    <li>Count and circle the correct number of objects</li>
                                    <li>Trace and write numbers from 1 to 50</li>
                                    <li>Match numbers to groups of objects</li>
                                    <li>Identify missing numbers in sequences</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                <ul>
                                    <li>Use blocks, spoons, buttons, or toys for real counting practice</li>
                                    <li>Count out loud together during walks, playtime, or snacks</li>
                                    <li>Use rhymes and songs to make counting fun and memorable</li>
                                    <li>Make mistakes okay — praise effort and progress</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Place toys or snacks in front of your child and ask them to count aloud. For example, <strong>“Can you give me 5 grapes?”</strong> This makes counting part of everyday learning!
                                </p>
                            </article>
                            <h2 className="myHeading" style={{fontSize: "3.5rem"}}>{questionNumber}</h2>
                            <div>
                                <div className="mt-4 mb-5 " style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {options.map((option, index) => (
                                        <div className="background-blue" style={{ width: 'auto', padding: '20px' }} key={index} onClick={() => checkAnswer(option)}>
                                            {option}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default NumberNames;
